<script setup>

    import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
    import { storeToRefs } from 'pinia'

    import LatteStyledContainer from '@/Components/App/LatteStyledContainer.vue'
    import LatteClientContacts from '@/Components/Contacts/LatteClientContacts.vue'
    import LatteClientSchedule from '@/Components/Client/LatteClientSchedule.vue'

    import { UserPlusIcon } from '@heroicons/vue/20/solid'

    import { useCaregiverStore, useClientSingleStore, useNotificationStore, useStaffStore, useViewStore } from '@/Stores'

    import { useRoute } from 'vue-router'
    import { util } from '@/Helpers'
    import WeekPicker from '@/Components/WeekPicker.vue'
    import LatteClientCareNeed from '@/Components/Client/LatteClientCareNeed.vue'
    import { useJobRunner } from '@/Helpers/jobRunner'

    const scheduleDate = ref(util.date.lastSunday())
    const viewStore = useViewStore()
    const clientSingleStore = useClientSingleStore()
    const caregiverStore = useCaregiverStore()
    const staffStore = useStaffStore()
    const route = useRoute()

    const notificationStore = useNotificationStore()

    const { loading, client } = storeToRefs(clientSingleStore)
    const { isEmbedded } = storeToRefs(viewStore)

    const addNew = ref(false)
    const contentRef = ref(null)

    async function hydrateClient(data) {
        loading.value = true
        await clientSingleStore.hydrate(data)
        loading.value = false
        sendHeight()
    }

    async function hydrateCaregiversAll(data) {
        loading.value = true
        await caregiverStore.hydrateAllCaregivers(data)
        loading.value = false
    }

    function rehydrateClient(data) {
        loading.value = true
        hydrateClient(data)
    }

    const jobRunner = useJobRunner('client')
    jobRunner.subscribe('addedRetryJob', function(errorCode, retryTime) {
        notificationStore.add('Error Saving Changes', 'error', `Retrying automatically... (${retryTime}/20)`, 15000)
        clientSingleStore.stopInactivityTimeout()
    })
    jobRunner.subscribe('finishedJobSuccess', function() {
        clientSingleStore.resetInactivityRefreshTimer()
    })
    jobRunner.subscribe('addedJob', function() {
        clientSingleStore.resetInactivityRefreshTimer()
        notificationStore.removeById(clientSingleStore.getMaxRetryDialogId())
        clientSingleStore.removeMaxRetryDialogId()
    })
    jobRunner.subscribe('reachedMaxRetry', function() {
        const maxRetryDialogId = notificationStore.add('Error Saving Changes', 'error', 'Make an edit to try again.', 7 * 24 * 60 * 60 * 1000)
        clientSingleStore.setMaxRetryDialogId(maxRetryDialogId)
    })

    function updateClient(data, formId) {
        // Guard: Prevent update if client data hasn't loaded yet so no new client is created
        if (!client.value.id || client.value.fe_generated) {
            return
        }

        clientSingleStore.applyLocalChangeToActiveClient(data)
        jobRunner.addJob('update', client.value.uuid, data, [formId], async (data, formId, jobId) => {
            await clientSingleStore.updateClientUsingUUID(client.value.uuid, data, [], formId, jobId)
                .then(function (response) {
                    if (jobRunner.hasScheduleJob('update', client.value.uuid) == false) {
                        hydrateClient(response.data)
                    }
                    jobRunner.handleJobSuccess('update', client.value.uuid, jobId)
                    notificationStore.add('Changes Saved')
                })
                .catch(function(error) {
                    jobRunner.handleJobError('update', client.value.uuid, jobId, error.error.response?.status)
                })
        })
    }

    function cancelClientUpdate(clientId) {
        jobRunner.removeJob('update', clientId)
    }

    const sendHeight = () => {
        nextTick(() => {
            const height = contentRef.value?.scrollHeight + 100 || 0;
            window.parent.postMessage(
                {
                    action: 'clientProfileIframeHeightChanged',
                    height: height
                },
                '*'
            )
        })
    }

    function handleSmsTrigger(cellPhone) {
        window.parent.postMessage({
            'action' : 'addPhoneNumberToSms',
            'number': cellPhone,
        }, '*')
    }

    onMounted(async () => {
        clientSingleStore.getClientByCoffeeUserProfileId(route.params.coffee_client_user_profile_id)
            .then(response => {
                rehydrateClient(response.data)
            })

        caregiverStore.getAllCaregivers()
            .then(response => {
                hydrateCaregiversAll(response.data)
            })

        sendHeight()
        window.addEventListener('resize', sendHeight)

        staffStore.getAllStaffs()
            .then(response => {
                staffStore.hydrateAllStaffs(response.data)
            })

    })

    onBeforeUnmount(() => {
        window.removeEventListener('resize', sendHeight)
    })

    const scheduleBuilderMode = ref(null)

</script>

<template>

    <div class="space-y-8" ref="contentRef">

        <div
            class="mx-auto max-w-7xl pb-2"
            :class="{
                'pt-0': isEmbedded,
                'sm:pt-4 md:pt-6 md:px-8': !isEmbedded
            }"
        >
            <div class="mx-auto max-w-5xl">
                <LatteStyledContainer
                    id="latte-client-contacts"
                    title="Contacts"
                    :forceSingleLineMobileLayout="true"
                    :isEmbedded="isEmbedded"
                    :centerHeaderSlot="false"
                >
                    <template #header>
                        <button
                            class="rounded bg-white black py-1 flex space-x-1 items-center text-xs font-bold px-4"
                            @click="addNew = true"
                        >
                            <span>Add New</span> <UserPlusIcon class="block h-6 w-5 pr-1" aria-hidden="true"/>
                        </button>
                    </template>
                    <template #default>
                        <LatteClientContacts
                            :addNew="addNew"
                            @update:selected="() => {addNew = false}"
                            @sms:triggered="handleSmsTrigger"
                            :showNotification="!isEmbedded"
                            :smsEnabled="isEmbedded"
                        />
                    </template>
                </LatteStyledContainer>
            </div>
        </div>

        <div
            class="mx-auto max-w-7xl pb-4"
            :class="{
                'pt-0': isEmbedded,
                'sm:pt-4 md:pt-6 md:px-8': !isEmbedded
            }"
        >
            <div class="mx-auto max-w-5xl">
                <LatteStyledContainer
                    id="care_need"
                    title="Care Needs"
                    :isEmbedded="isEmbedded"
                    :forceSingleLineMobileLayout="true"
                >
                    <template #default>
                        <LatteClientCareNeed
                            @client:update="updateClient"
                            @client:cancel-update="cancelClientUpdate"
                        />
                    </template>
                </LatteStyledContainer>
            </div>
        </div>

        <div
            :class="[
                'mx-auto max-w-7xl',
                {
                    'pt-0': isEmbedded,
                    'sm:pt-4 md:pt-6 md:px-8': !isEmbedded
                }
            ]"
        >
            <div class="mx-auto max-w-5xl">
                <LatteStyledContainer
                    id="schedule"
                    title="Care Schedule"
                    :isEmbedded="isEmbedded"
                    :forceSingleLineMobileLayout="true"
                >
                    <template #header>
                        <div class="w-full flex justify-center items-center">
                            <WeekPicker
                                v-model="scheduleDate"
                                :disabled="scheduleBuilderMode == 'edit'"
                            />
                        </div>
                    </template>
                    <template #default>
                        <LatteClientSchedule
                            :client_id="Number(client.id)"
                            :start_date="scheduleDate"
                            @builder-mode:change="(mode) => scheduleBuilderMode = mode"
                        />
                    </template>
                </LatteStyledContainer>
            </div>
        </div>

    </div>

</template>
